import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";

import moment from "moment-timezone";
import Time from "lib/time";

import Autocomplete from "@material-ui/lab/Autocomplete";
import Chip from "@material-ui/core/Chip";
import ChipInput from "material-ui-chip-input";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";

import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormGroup from "@material-ui/core/FormGroup";
import InputLabel from "@material-ui/core/InputLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import CloseIcon from "@material-ui/icons/Close";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import { OPERATORS } from "utils/constants/terinaryOperators";

const searchStyles = makeStyles((theme) => ({
  searchField: {
    width: "50ch",
    marginTop: theme.spacing(1),
  },
}));

export const SearchField = (props) => {
  const classes = searchStyles();
  const debounce = 250;

  const [value, setValue] = useState("");
  const [debounceTimer, setDebounceTimer] = useState(null);

  const updateValue = (event) => {
    const textValue = event ? event.target.value : "";
    setValue(textValue);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    setDebounceTimer(
      setTimeout(() => {
        if (textValue.length > 0) {
          props.onChange(textValue.toLowerCase().split(" "));
        } else {
          props.onChange([]);
        }
        setDebounceTimer(null);
      }, debounce)
    );
  };

  return (
    <FormControl className={classes.searchField}>
      <TextField
        onChange={updateValue}
        variant="standard"
        size="small"
        value={value}
        label="Search"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" onClick={() => updateValue()}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </FormControl>
  );
};

export const LogTypeOption = (props) => {
  return (
    <div
      data-cy={"reportBuilderOption"} // For selecting with cypress
    >
      <Typography variant="h6">{props.display_name || props.name}</Typography>
      <Typography variant="body2" color="textSecondary">
        {props.description}
      </Typography>
      <Typography variant="overline">
        Categories: {props.category_names.join(", ")}
      </Typography>
    </div>
  );
};

export const ColumnOption = (props) => {
  return (
    <div>
      <Typography variant="h6">{props.display_name || props.name}</Typography>
      <Typography variant="body2" color="textSecondary">
        {props.logTypes.join(", ")}
      </Typography>
      <Typography variant="overline">
        Operators: {props.valid_operator_names.join(", ")}
      </Typography>
    </div>
  );
};

const filterFieldStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1),
  },
  formControl: {
    margin: theme.spacing(1),
  },
}));

export const FilterField = (props) => {
  // const [idx, setIdx] = useState(props.idx)
  const [searchField, setSearchField] = useState(0);
  const [opcode, setOpcode] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [fields, setFields] = useState([]);

  const buttonRef = useRef(null);

  const classes = filterFieldStyles();

  useEffect(() => {
    props.fields.sort((a, b) =>
      (a.display_name || a.name).localeCompare(b.display_name || b.name)
    );
    props.fields.map((f) =>
      f.valid_operator_names.sort((a, b) =>
        OPERATORS[a].display_name > OPERATORS[b].display_name ? 1 : -1
      )
    );
    setFields(props.fields);

    if (props.fields.length > 0) {
      setOpcode(props.fields[0].valid_operator_names[0]);
    }
  }, [props.fields]);

  const handleFieldChange = (event) => {
    const idx = event.target.value;
    setSearchField(idx);
    const op = fields[idx].valid_operator_names[0];
    setOpcode(op);
    if (
      ["array_only_includes", "!array_only_includes", "in", "!in"].includes(op)
    ) {
      setSearchValue([]);
    } else {
      setSearchValue("");
    }
  };

  const handleOpchange = (event) => {
    const op = event.target.value;
    setOpcode(op);
    if (
      ["array_only_includes", "!array_only_includes", "in", "!in"].includes(op)
    ) {
      setSearchValue([]);
    } else {
      setSearchValue("");
    }
  };

  const handleAddChip = (chip) => {
    if (["INT64", "FLOAT64"].includes(fields[searchField].data_type)) {
      chip = Number(chip);
    }
    searchValue.push(chip);
    setSearchValue(searchValue);
  };

  const handleDeleteChip = (chip, index) => {
    searchValue.pop(index);
    setSearchValue(searchValue);
  };

  const handleChange = (event) => {
    if (
      ["INT64", "FLOAT64"].includes(fields[searchField].data_type) ||
      ["array_length_greater_than", "!array_length_greater_than"].includes(
        opcode
      )
    ) {
      setSearchValue(Number(event.target.value));
    } else if (fields[searchField].data_type === "BOOL") {
      setSearchValue(!!event.target.checked);
    } else {
      setSearchValue(event.target.value);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") buttonRef.current.click();
  };

  const renderEditField = () => {
    if (
      ["array_only_includes", "!array_only_includes", "in", "!in"].includes(
        opcode
      )
    ) {
      if (fields[searchField].options) {
        return (
          <Autocomplete
            multiple={true}
            options={fields[searchField].options}
            freeSolo={true}
            getOptionLabel={(option) => option.label}
            onChange={(e, v) => {
              setSearchValue(v.map((_v) => _v.value));
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
            getOptionSelected={(option, value) => option.value === value}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  key={option.label}
                  label={option.label}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        );
      }
      return (
        <ChipInput
          onAdd={handleAddChip}
          onDelete={handleDeleteChip}
          blurBehavior="add"
          value={searchValue}
        />
      );
    } else {
      if (fields[searchField].data_type === "BOOL") {
        return (
          <Switch
            onChange={handleChange}
            checked={!!searchValue}
            color="primary"
          />
        );
      } else if (fields[searchField].options) {
        return (
          <FormControl>
            <Select onChange={handleChange} value={searchValue}>
              {fields[searchField].options.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else {
        var fieldType = "string";
        if (
          ["INT64", "FLOAT64"].includes(fields[searchField].data_type) ||
          ["array_length_greater_than", "!array_length_greater_than"].includes(
            opcode
          )
        ) {
          fieldType = "number";
        }

        return (
          <TextField
            type={fieldType}
            onChange={handleChange}
            value={searchValue}
          />
        );
      }
    }
  };

  const addFilter = () => {
    props.addFilter(fields[searchField], opcode, searchValue);
  };

  if (fields.length > 0) {
    return (
      <Paper className={classes.paper}>
        <FormControl className={classes.formControl}>
          <Select onChange={handleFieldChange} value={searchField}>
            {fields.map((field, idx) => (
              <MenuItem key={field.name} aria-label={field.name} value={idx}>
                {field.display_name || field.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={classes.formControl}>
          <Select onChange={handleOpchange} value={opcode}>
            {fields[searchField] &&
              fields[searchField].valid_operator_names.map((op) => (
                <MenuItem
                  key={op}
                  aria-label={OPERATORS[op].display_name}
                  value={op}
                >
                  {OPERATORS[op].display_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl
          className={classes.formControl}
          onKeyPress={handleKeyPress}
        >
          {renderEditField()}
        </FormControl>
        <FormControl className={classes.formControl}>
          <Button ref={buttonRef} variant="outlined" onClick={addFilter}>
            Add
          </Button>
        </FormControl>
      </Paper>
    );
  } else {
    return <div />;
  }
};

const timespanPickerStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
  },
  quickSelectItem: {
    textTransform: "capitalize",
    backgroundColor: theme.palette.background.paper,
  },
  presetContainer: {
    display: "flex",
    cursor: "pointer",
    alignItems: "center",
  },
  inputContainer: {
    display: "flex",
    marginRight: 10,
    flexDirection: "column",
    alignItems: "flex-start",
  },
  labelText: {
    fontSize: 14,
    marginBottom: 5,
  },
  textContainer: {
    minWidth: 250,
    display: "flex",
    borderRadius: 10,
    alignItems: "center",
    padding: "10px 15px",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    "& p": {
      fontSize: 15,
      marginBottom: 0,
    },
  },
  newSearchField: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  customSelectOption: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  errorMessage: {
    margin: 0,
    paddingLeft: 20,
    color: theme.palette.error.main,
  },
}));

const defaultDeltas = [
  "@:-10",
  "@-1",
  "@-4",
  "@-24",
  "-7:d",
  "-30:d",
  "-1:w",
  "0:m",
  "-1:m",
  "0:q",
  "-1:q",
];

export const TimespanPicker = (props) => {
  const deltas =
    props.deltas === undefined
      ? defaultDeltas.map((d) => new Time(d))
      : props.deltas.map((d) => new Time(d));

  const [selectedDelta, setSelectedDelta] = useState("-30:d");
  const [customDateRange, setCustomDateRange] = useState(false);
  const [startTime, setStartTime] = useState(props.startTime);
  const [endTime, setEndTime] = useState(props.endTime);
  const [errorMessage, setErrorMessage] = useState(null);

  const classes = timespanPickerStyles();

  useEffect(() => {
    const dMatch = deltas.find((d) => {
      const ranges = d.getTimeRange();
      return (
        ranges.startTime.diff(props.startTime || startTime, "minutes") === 0 &&
        ranges.endTime.diff(props.endTime || endTime, "minutes") === 0
      );
    });

    if (dMatch) {
      const ranges = dMatch.getTimeRange();
      setStartTime(ranges.startTime);
      setEndTime(ranges.endTime);
      setSelectedDelta(dMatch.timeString);
    } else {
      setStartTime(props.startTime);
      setEndTime(props.endTime);
      setSelectedDelta("custom");
    }
  }, [props.startTime, props.endTime]);

  useEffect(() => {
    if (props.selectedDelta) {
      if (defaultDeltas.includes(props.selectedDelta)) {
        setSelectedDelta(props.selectedDelta);
      } else {
        setSelectedDelta("custom");
      }
    }
  }, [props.selectedDelta]);

  const quickDateClickHandler = (e) => {
    if (defaultDeltas.includes(e.target.value)) {
      const t = new Time(e.target.value);
      setSelectedDelta(t.timeString);
      const ranges = t.getTimeRange();
      setTimes(ranges.startTime, ranges.endTime);
    }
  };

  const setTimes = (startTime, endTime) => {
    if (startTime > endTime)
      return setErrorMessage(
        "Oops, please ensure your start time begins before your end time"
      );
    else setErrorMessage(null);
    setStartTime(startTime);
    setEndTime(endTime);
    props.setTimes(startTime, endTime);
    handleCustomDateRangeClose();
  };

  const handleCustomDateRangeClose = () => {
    setErrorMessage(null);
    setCustomDateRange(false);
  };

  const handleSetCustomDateRange = () => {
    setCustomDateRange([startTime, endTime]);
  };

  const selectCustomDateRange = () => {
    setTimes(customDateRange[0], customDateRange[1]);
    setSelectedDelta("custom");
  };

  return (
    <div className={classes.root} {...props}>
      {props.isNorthStar ? (
        <div className={classes.presetContainer}>
          <div className={classes.inputContainer}>
            <p className={classes.labelText}>Time range</p>
            <Select
              id="datetime-select"
              value={selectedDelta}
              labelId="datetime-select-label"
              onChange={quickDateClickHandler}
              className={classes.quickSelectItem}
              MenuProps={{ disableScrollLock: true }}
              style={{ borderRadius: 10, height: 43 }}
            >
              <MenuItem
                value="custom"
                className={classes.customSelectOption}
                onClick={handleSetCustomDateRange}
              >
                Custom
              </MenuItem>
              {deltas.map((d, i) => (
                <MenuItem
                  key={i}
                  className={classes.quickSelectItem}
                  value={d.timeString}
                >
                  {d.language()}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      ) : (
        <FormControl>
          <InputLabel id="datetime-select-label">{"Time Range"}</InputLabel>
          <Select
            MenuProps={{ disableScrollLock: true }}
            labelId="datetime-select-label"
            id="datetime-select"
            value={selectedDelta}
            datacy={"datetimeSelect"}
            onChange={quickDateClickHandler}
            className={classes.quickSelectItem}
          >
            <MenuItem value="custom" onClick={handleSetCustomDateRange}>
              Custom
            </MenuItem>
            {deltas.map((d, i) => (
              <MenuItem
                key={d.timeString}
                datacy={`datetimeSelectOption-${i}`}
                className={classes.quickSelectItem}
                value={d.timeString}
              >
                {d.language()}
              </MenuItem>
            ))}
          </Select>
          {!!startTime && !!endTime && (
            <FormHelperText
              datacy={`formHelperText`}
              style={{ paddingLeft: 10 }}
            >{`${startTime.format("l, LT")} - ${endTime.format(
              "l, LT"
            )}`}</FormHelperText>
          )}
        </FormControl>
      )}

      <Dialog
        disableScrollLock={true}
        onClose={handleCustomDateRangeClose}
        aria-labelledby="custom-daterange-picker"
        open={customDateRange}
      >
        <DialogTitle id="custom-daterange-picker-title">
          Select Date Range
        </DialogTitle>
        {!!errorMessage && (
          <p className={classes.errorMessage}>{errorMessage}</p>
        )}
        <FormGroup row>
          <div>
            <FormControl component="fieldset" className={classes.formControl}>
              <TextField
                id="date-start"
                label="Start Date"
                type="date"
                value={
                  customDateRange ? customDateRange[0].format("YYYY-MM-DD") : ""
                }
                onChange={(event) => {
                  let time = event.target.value;
                  if (!time) return;
                  const [year, month, date] = time.split("-");
                  // subtract one here because months are zero indexed
                  // per moment js documentation but month extracted
                  // from target value is not i.e. August = 7
                  let newTime = moment(customDateRange[0]).set({
                    year: year,
                    month: month - 1,
                    date: date,
                  });
                  setCustomDateRange([newTime, customDateRange[1]]);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
            <FormControl component="fieldset" className={classes.formControl}>
              <TextField
                id="time-start"
                label="Start Time"
                type="time"
                value={
                  customDateRange ? customDateRange[0].format("HH:mm") : ""
                }
                onChange={(event) => {
                  let time = event.target.value;
                  if (!time) return;
                  const [hour, minute] = time.split(":");
                  let newTime = moment(customDateRange[0])
                    .set("hour", hour)
                    .set("minute", minute);
                  setCustomDateRange([newTime, customDateRange[1]]);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </div>

          <div>
            <FormControl component="fieldset" className={classes.formControl}>
              <TextField
                id="date-end"
                label="End Date"
                type="date"
                value={
                  customDateRange ? customDateRange[1].format("YYYY-MM-DD") : ""
                }
                onChange={(event) => {
                  let time = event.target.value;
                  if (!time) return;
                  const [year, month, date] = time.split("-");
                  // subtract one here because months are zero indexed
                  // per moment js documentation but month extracted
                  // from target value is not i.e. August = 7
                  let newTime = moment(customDateRange[1]).set({
                    year: year,
                    month: month - 1,
                    date: date,
                  });
                  setCustomDateRange([customDateRange[0], newTime]);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
            <FormControl component="fieldset" className={classes.formControl}>
              <TextField
                id="time-end"
                label="End Time"
                type="time"
                value={
                  customDateRange ? customDateRange[1].format("HH:mm") : ""
                }
                onChange={(event) => {
                  let time = event.target.value;
                  if (!time) return;
                  const [hour, minute] = time.split(":");
                  let newTime = moment(customDateRange[1])
                    .set("hour", hour)
                    .set("minute", minute);
                  setCustomDateRange([customDateRange[0], newTime]);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </div>
        </FormGroup>
        <DialogActions>
          <Button onClick={handleCustomDateRangeClose} color="primary">
            Cancel
          </Button>
          <Button onClick={selectCustomDateRange} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
